import React, { useEffect, useState } from "react";
import { api, absUrl } from "@/lib/api";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const wordCount = (t) => (t || "").split(/\s+/).filter(Boolean).length;

export default function ProfileSetupModal({ firstTime, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [countries, setCountries] = useState([]);
  const [maxWords, setMaxWords] = useState(30);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/supplier/profile").then(({ data }) => {
      setCountries(data.countries || []);
      setMaxWords(data.max_words || 30);
      setForm({ logo_url: data.logo_url, city: data.city, country_code: data.country_code, description: data.description, website: data.website, tier: data.tier, custom_oem: !!data.custom_oem });
    }).catch(() => toast.error("Could not load your profile"));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const words = wordCount(form?.description);
  const over = words > maxWords;

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    if (over) return;
    setSaving(true);
    try {
      await api.put("/supplier/profile", { logo_url: form.logo_url, city: form.city, country_code: form.country_code, description: form.description, website: form.website, custom_oem: !!form.custom_oem });
      toast.success("Profile saved — your public listing is updated");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  const skip = async () => {
    try { await api.post("/supplier/profile/skip"); } catch { /* ignore */ }
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 flex items-center justify-center p-4" data-testid="profile-setup-modal">
      <form onSubmit={save} className="bg-white w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 pt-6 pb-4 border-b border-[#E8ECE9] flex items-start justify-between gap-4">
          <div>
            <div className="tracking-overline text-[10px] text-[#B35C4A]">{firstTime ? "Welcome — one quick step" : "Your profile"}</div>
            <h2 className="font-serif text-2xl text-[#11211A] mt-1">{firstTime ? "Set up your supplier profile" : "Edit your supplier profile"}</h2>
            <p className="text-sm text-[#7D8C85] mt-1">This is what buyers see on your public listing. Changes go live the moment you save.</p>
          </div>
          {!firstTime && <button type="button" onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="profile-setup-close"><X className="w-5 h-5" /></button>}
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5" data-testid="profile-setup-body">
          {!form ? <div className="text-sm text-[#7D8C85]">Loading…</div> : (
            <>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-2">Company logo</label>
                <div className="flex items-center gap-3">
                  {form.logo_url ? (
                    <div className="w-20 h-20 border border-[#E8ECE9] bg-white flex items-center justify-center relative" data-testid="profile-logo-preview">
                      <img src={absUrl(form.logo_url)} alt="logo" className="max-w-full max-h-full object-contain" />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} className="absolute -top-2 -right-2 bg-white border border-[#E8ECE9] text-[#B35C4A] text-xs w-5 h-5 rounded-full" data-testid="profile-logo-remove">×</button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 border border-dashed border-[#C4CFC9] flex items-center justify-center text-[#C4CFC9] text-[10px] uppercase tracking-wider">No logo</div>
                  )}
                  <label className={`btn-secondary text-xs cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="profile-logo-upload-btn">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {form.logo_url ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadLogo} data-testid="profile-logo-input" />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">City</label>
                  <input value={form.city} onChange={set("city")} placeholder="e.g. Manchester" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="profile-city" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Country</label>
                  <select value={form.country_code} onChange={set("country_code")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="profile-country">
                    <option value="">Select country…</option>
                    {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-[#4A5550]">What you sell or do</label>
                  <span className={`text-[11px] tabular-nums ${over ? "text-[#B35C4A] font-semibold" : "text-[#7D8C85]"}`} data-testid="profile-word-count">{words} / {maxWords} words</span>
                </div>
                <textarea value={form.description} onChange={set("description")} rows={3} placeholder="e.g. Hotel-grade bed linen, towels and bathrobes for boutique hotels and serviced apartments across the UK." className={`w-full px-3 py-2 text-sm border rounded-sm ${over ? "border-[#B35C4A]" : "border-[#C4CFC9]"}`} data-testid="profile-description" />
                {over && <div className="text-[11px] text-[#B35C4A] mt-1" data-testid="profile-word-count-error">Please trim to {maxWords} words or fewer.</div>}
              </div>

              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Website</label>
                <input value={form.website} onChange={set("website")} placeholder="www.yourcompany.com" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="profile-website" />
                {form.tier === "free" && <div className="text-[11px] text-[#7D8C85] mt-1">Shown as text on your listing. Upgrade to Founding Supplier for a clickable link.</div>}
              </div>

              <label className="flex items-start gap-2.5 p-3 border border-[#E8ECE9] bg-[#FAFAF7] cursor-pointer" data-testid="profile-custom-oem-label">
                <input type="checkbox" checked={!!form.custom_oem} onChange={(e) => setForm((f) => ({ ...f, custom_oem: e.target.checked }))} className="accent-[#264839] mt-0.5" data-testid="profile-custom-oem" />
                <span>
                  <span className="block text-sm font-medium text-[#11211A]">Custom / OEM available</span>
                  <span className="block text-[11px] text-[#7D8C85]">Tick if you make custom or private-label products. Buyers can filter for this and see a “Custom / OEM” label on your card.</span>
                </span>
              </label>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E8ECE9] bg-[#FAFAF7] flex items-center justify-between gap-3">
          {firstTime
            ? <button type="button" onClick={skip} className="text-xs text-[#7D8C85] hover:text-[#11211A] underline" data-testid="profile-setup-skip">I'll do this later</button>
            : <button type="button" onClick={onClose} className="btn-ghost text-xs" data-testid="profile-setup-cancel">Cancel</button>}
          <button type="submit" disabled={!form || saving || uploading || over} className="btn-primary text-xs disabled:opacity-50" data-testid="profile-setup-save">
            {saving ? "Saving…" : firstTime ? "Save & go to dashboard" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
